import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { ApiError } from '../api/client'
import { onsitePresenceQuery, type PresenceBox } from '../api/proactivitee'
import { Forbidden } from '../ui/Forbidden'
import { SelectPicker } from '../ui/SelectPicker'
import { Content } from '../ui/Shell'
import { Spinner } from '../ui/Spinner'
import { assetUrl } from '../ui/assetUrl'
import './OnsitePresencePage.css'

/**
 * Prezenta tehnicienilor - `Dashboards::onsite_presence`, la
 * `/app/dashboards/onsite_presence`.
 *
 * Aceeasi cutie pe om ca `dashboards/monitor/dashboard.php` (clasele
 * `adminBox*`, cu foaia lor de acolo, adusa mai jos), aceleasi iconite din
 * `images/dashboards/`, aceeasi improspatare la un minut.
 *
 * FILTRUL de departamente sta in ADRESA (`?departments=1,2`), nu in Redis pe
 * om, ca vederea sa se poata trimite unui coleg. Fara departamente alese
 * lista e goala, ca in portal - de-aia se spune ce e de facut.
 */

const ICON: Record<string, string> = {
  out: 'holiday-icon.png',
  home: 'house-icon.png',
  absent: 'house-icon-red.png',
  commuting: 'running-icon.png',
  onsite: 'working-icon.png',
}

function icon(state: string): string {
  return assetUrl('images/dashboards/' + (ICON[state] ?? ICON.absent))
}

function departmentsFromLocation(): number[] {
  const raw = new URLSearchParams(window.location.search).get('departments') ?? ''
  return raw
    .split(',')
    .map((v) => parseInt(v, 10))
    .filter((v) => v > 0)
}

export function OnsitePresencePage() {
  const selected = departmentsFromLocation()
  const page = useQuery({
    ...onsitePresenceQuery(selected),
    /* `setInterval(refreshDashboard, 60000)` din `monitor/index-1.js`. */
    refetchInterval: (q) => (q.state.data?.refresh ?? 60) * 1000,
  })
  const [showFilters, setShowFilters] = useState(false)
  const [picked, setPicked] = useState<string[]>(selected.map(String))

  if (page.error instanceof ApiError && page.error.status === 403) return <Forbidden />
  if (!page.data) return <Spinner />

  function apply() {
    const q = picked.length > 0 ? `?departments=${picked.join(',')}` : ''
    window.location.assign(`/app/dashboards/onsite_presence${q}`)
  }

  return (
    <Content>
      <div className="filtersApplied">
        {page.data.selected.length > 0 && (
          <span className="text-muted">
            Departments:{' '}
            {page.data.departments
              .filter((d) => page.data!.selected.includes(d.ID))
              .map((d) => d.Nume)
              .join(', ')}
          </span>
        )}
      </div>

      <div className="actions" style={{ textAlign: 'right', marginBottom: 20, marginTop: 30 }}>
        <button type="button" className="btn btn-sm btn-primary toggleFilters" onClick={() => setShowFilters((s) => !s)}>
          <i className="fa fa-filter"></i> Filters
        </button>
      </div>

      <div className="generalFilters" hidden={!showFilters}>
        <div className="panel panel-default">
          <header className="panel-heading sm bg-default">
            <h4>
              <i className="fa fa-filter"></i> <strong>Apply</strong> filters
            </h4>
          </header>
          <div className="panel-body">
            <form
              className="form-horizontal labelcustomize tk-action-form"
              id="generalFilters"
              onSubmit={(e) => {
                e.preventDefault()
                apply()
              }}
            >
              <div className="form-group">
                <div className="col-md-4">
                  <label>Departments</label>
                  <SelectPicker
                    id="departmentsFilter"
                    title="Departments"
                    multiple
                    value={picked}
                    onChange={setPicked}
                    options={page.data.departments.map((d) => ({ value: String(d.ID), label: d.Nume }))}
                  />
                </div>
              </div>
              <div className="form-group offset">
                <div className="col-md-offset-5 col-md-7">
                  <button className="btn btn-danger btn-sm" type="submit">
                    Apply
                  </button>
                  <button className="btn btn-inverse btn-sm resetFilters" type="button" onClick={() => setPicked([])}>
                    Reset
                  </button>
                  <button className="btn btn-default btn-sm closeFilters" type="button" onClick={() => setShowFilters(false)}>
                    Cancel
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>

      <div id="content">
        <div className="monitor-dashboard">
          <div className="legend">
            <span>
              <img src={icon('out')} alt="" /> Out of office
            </span>
            <span>
              <img src={icon('home')} alt="" /> End activity
            </span>
            <span>
              <img src={icon('absent')} alt="" /> Not present today
            </span>
            <span>
              <img src={icon('commuting')} alt="" /> Commuting
            </span>
            <span>
              <img src={icon('onsite')} alt="" /> Onsite
            </span>
          </div>
          <div className="row">
            {page.data.techs.length === 0 ? (
              <span className="noInfo">
                {page.data.selected.length === 0 ? 'Pick the departments to watch, under Filters.' : 'No info available.'}
              </span>
            ) : (
              page.data.techs.map((t) => <TechBox tech={t} key={t.id} />)
            )}
          </div>
        </div>
      </div>
    </Content>
  )
}

function TechBox({ tech }: { tech: PresenceBox }) {
  return (
    <div className="adminParentBox col-md-3" data-tech={tech.id} data-state={tech.state}>
      <div className="adminBox">
        <div className="adminBox-top">
          {tech.photo !== '' && <img className="photo" src={tech.photo} alt="" />}
          <div className="name fade-text">
            <label className="firstname">{tech.firstName}</label> <label className="lastname">{tech.lastName}</label>
            <p>
              {tech.opened} {tech.opened === 1 ? 'ticket' : 'tickets'} ({tech.planned} planned)
            </p>
          </div>
          <div className="adminBox-middle">
            <div className="middleRight">
              <span className="imgLine">
                <img src={icon(tech.state)} title={tech.activity} alt="" />
              </span>
              <span className="firstLine">{tech.activity}</span>
              <span className="secondLine">
                <label>{tech.where}</label>
              </span>
            </div>
          </div>
        </div>
        <div className="adminBox-bottom">
          <div className="col-md-4 noMargin noPadding">
            <span className="title">First CheckIn</span>
            <br />
            <span className={`subtitle ${tech.firstLate ? 'red' : 'color'}`}>{tech.firstCheckin}</span>
          </div>
          <div className="col-md-4 noMargin noPadding">
            <span className="title">{tech.arrivedLabel}</span>
            <br />
            <span className="subtitle">{tech.arrivedAt}</span>
          </div>
          <div className="col-md-4 noMargin noPadding">
            <span className="title">Estimated time</span>
            <br />
            <span className={`subtitle ${tech.estimatedLate ? 'red' : 'color'}`}>{tech.estimated}</span>
          </div>
        </div>
        <div className="adminBox-bottom white-bg">
          <div className="col-md-3 noMargin noPadding">
            <span className="title">Evaluation</span>
            <br />
            <span>
              <i className="fa fa-star">
                <label>{tech.rating}</label>
              </i>
            </span>
          </div>
          <div className="col-md-3 noMargin noPadding">
            <span className="title">Site visits</span>
            <br />
            <span>
              <i className="fa fa-map-marker">
                <label>{tech.visits}</label>
              </i>
            </span>
          </div>
          <div className="col-md-3 noMargin noPadding">
            <span className="title">Proactive int.</span>
            <br />
            <span>
              <i className="fas fa-ticket-alt">
                <label>{tech.proactive}</label>
              </i>
            </span>
          </div>
          <div className="col-md-3 noMargin noPadding">
            <span className="title">Requests</span>
            <br />
            <span>
              <i className="fas fa-ticket-alt">
                <label>{tech.requests}</label>
              </i>
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}
